// src/pages/ActivityLog.jsx
// Users → Activity log. Super Admins see everything; roles with "Activity
// log" (Admins by default) see their own account's activity, without Super
// Admin actions (the server filters it). Two kinds of rows (3.9):
//   Changes          who did what to whom: people added and removed, roles,
//                    two-factor resets, access requests, settings, deleted
//                    and restored faxes, View as
//   Portal activity  what people did in the portal: signing in and out,
//                    opening tabs, sending and resending faxes, viewing and
//                    downloading faxes and reports
// Filters (kind, person, dates) are applied by the server; "Load more"
// fetches older rows. Read-only.
import MultiSearchSelect from '../components/MultiSearchSelect';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { fetchActivity, downloadSheet } from '../utils/api';
import { timeZone, tzLabel } from '../utils/timeZone';
import { PILL, PILL_ON, PILL_ROW, TH_SELECT, TH_OPTION, SECTION_TITLE, SECTION_SUB } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

const ACTIONS = {
  added                  : 'Added to the portal',
  restored               : 'Access given back',
  removed                : 'Access removed',
  access_blocked         : 'Access blocked after repeated failed faxes',   // 3.57.9
  deactivated            : "Deactivated: email address doesn't exist",   // 3.62.18 (the system)
  access_unblocked       : 'Unblocked after repeated failed faxes',
  role_changed           : 'Role changed',
  reset_2fa              : 'Two-factor reset',
  recovery_codes_created : 'New recovery codes made',
  reset_link_sent        : 'Two-factor reset link emailed',
  backup_code_sent       : 'Backup sign-in code sent',   // 3.85.0
  backup_code_signin     : 'Signed in with a backup code',
  whatsapp_changed       : 'WhatsApp number changed',
  whatsapp_confirmed     : 'WhatsApp number confirmed',
  reset_requested        : 'Asked for a two-factor reset',
  access_requested       : 'Asked for a portal account',
  access_approved        : 'Account request approved',
  access_declined        : 'Account request declined',
  role_created           : 'Role added',
  role_deleted           : 'Role deleted',
  role_permissions_changed: 'Role permissions changed',
  role_reset_to_defaults : 'Role reset to defaults',
  roles_copied           : 'Role settings copied from another group',
  fax_deleted            : 'Fax deleted',
  fax_restored           : 'Fax restored',
  view_as_start          : 'Started View as',
  view_as_end            : 'Ended View as',
  view_as_action         : 'Made a change using View as',
  invite_sent            : 'Invite sent',
  password_set_from_invite: 'Password set from invite',
  temp_password_changed  : 'Temporary password replaced',
  password_reset_by_admin: 'Password reset by an Admin',
  email_to_fax_deadline  : 'Email-to-fax date changed',
  unregistered_digest_sent: 'Emailed the list of senders without an account',
  billing_changed        : 'Billing changed',
  device_remembered      : 'Device remembered (no code for 30 days)',
  devices_forgotten      : 'Remembered devices forgotten',
  passkey_added          : 'Turned on Face ID / fingerprint unlock',   // 3.69.0
  passkey_removed        : 'Removed Face ID / fingerprint unlock',
  passkey_signin         : 'Signed in with a passkey',   // 3.74.4
  app_handoff            : 'Opened the portal from the CP Fax app',   // 3.82.0
  app_signin             : 'Signed in to the CP Fax app',
  app_handoff_refused    : 'Refused a sign-in code for the CP Fax app',
  sender_reminders_sent  : 'Emailed reminders to senders without an account',
  signed_out_by_admin    : 'Signed out by an Admin',
  twofa_reminders_sent   : 'Emailed people without two-factor',
  signin_invites_sent    : 'Invited people who never signed in',
  reminder_schedule_changed: 'Weekly reminder turned on or off',
  secondary_email_requested: 'Secondary email: confirm link sent',
  secondary_email_added  : 'Secondary email added',
  secondary_email_removed: 'Secondary email removed',
  people_report_downloaded: 'Downloaded the people report',
  followup_owners_set    : 'Follow-up owners set',
  account_created        : 'Client created',
  account_changed        : 'Client changed',
  email_default_changed  : 'Default account for emailed faxes changed',
  faxes_claimed          : 'Earlier faxes moved to client',
  retries_priority_changed: 'Priority fax numbers changed',
  send_timings_changed   : 'Sending timings changed',   // 3.75.0: Settings → Fax Settings → Sending Timings
  priority_number_changed: 'Priority number ★ changed',
  profile_picture_changed: 'Profile picture changed',
  team_created           : 'Team created',
  team_changed           : 'Team changed',
  team_deleted           : 'Team deleted',
  team_members_changed   : 'Team members changed',
  team_default_changed   : 'Default team changed',
  person_teams_changed   : "Someone's teams changed",
  kb_article_added       : 'Library article added',
  kb_article_edited      : 'Library article edited',
  kb_article_deleted     : 'Library article deleted',
  held_reminders_sent    : 'Emailed reminders about held faxes',
  inactive_reviews_sent  : 'Asked Admins to review inactive people',
  request_reminders_sent : 'Reminded approvers about access requests',
  // Portal activity
  signed_in              : 'Signed in',
  signed_out             : 'Signed out',
  tab_opened             : 'Opened a tab',
  fax_sent               : 'Sent a fax',
  support_opened         : 'Opened Support',   // 3.65.0
  contact_shared_name    : 'Saved a receiver name for a number',   // 3.66.0
  contact_added          : 'Added a contact',   // 3.68.7: these three were logged, shown as codes
  contact_changed        : 'Changed a contact',
  contact_listing_changed: 'Changed a phone listing (every client)',
  fax_resent             : 'Resent a fax',
  fax_sent_now           : 'Sent a fax in line now',   // 3.69.4
  microsoft_connected    : 'Connected Microsoft sign-in',   // 3.72.0
  microsoft_disconnected : 'Disconnected Microsoft sign-in',
  apple_connected        : 'Connected Apple sign-in',   // 3.87.0
  apple_disconnected     : 'Disconnected Apple sign-in',
  fax_viewed             : 'Viewed a fax',
  fax_downloaded         : 'Downloaded a fax',
  app_downloaded         : 'Downloaded an app',   // 3.83.2
  faxes_downloaded       : 'Downloaded several faxes',
  report_downloaded      : 'Downloaded a report',
  feedback_deleted       : 'Deleted feedback',
  email_template_test    : 'Sent a test email to themselves',
  email_template_saved   : 'Email template edited',
  email_template_reset   : 'Email template set back to the original',
  announcement_sent      : 'Announcement sent',
  announcement_test      : 'Sent a test announcement to themselves',
  announcement_scheduled : 'Scheduled an announcement',   // 3.52.5
  announcement_unscheduled: 'Cancelled an announcement\'s schedule',
  // 3.49.0: Security → IP Blocking (Super Admins only)
  ip_blocked             : 'IP address blocked',
  ip_unblocked           : 'IP address unblocked',
  ip_allowed             : 'IP address allowed (never blocked)',
  ip_allow_removed       : 'IP address taken off the allow list',
};
const RESET_METHODS = { recovery_code: 'with a recovery code', email_link: 'with an emailed link', admin: 'by an administrator' };

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) + ' ' + tzLabel(new Date(iso));
}

// 3.75.0: Sending Timings' names in the activity log
const TIMING_NAMES = { afterDelivered: 'After delivered (min)', afterFailed: 'After failed (min)', hardSpacing: 'Hard to reach (min apart)', retryFirst: 'First retry (min)', retryNext: 'Later retries (min)', retries: 'Retries', priorityRetries: '★ retries', guardFailures: 'Retry pause after', blockLimit: 'Block after', blockHours: 'Block (hours)', longRetryGap: 'Longer retries (min apart)', congAfterDelivered: 'Congestion: after delivered (min)', congAfterFailed: 'Congestion: after failed (min)' };

function describe(ev) {
  const d = ev.detail || {};
  switch (ev.action) {
    case 'reset_2fa': return RESET_METHODS[d.method] || '';
    case 'ip_blocked': return [d.kind === 'manual' ? 'by hand' : d.kind === 'spam' ? 'too many requests' : 'failed sign-ins', d.reason && d.kind !== 'manual' ? d.reason : '', d.until ? `until ${d.until === 'no end' ? 'no end' : formatDate(d.until)}` : '', d.note].filter(Boolean).join(' · ');
    case 'ip_allowed': return d.note || '';
    case 'role_changed': return `${d.from || '—'} → ${d.to || '—'}`;
    case 'added': case 'restored': return [d.group, d.role].filter(Boolean).join(' · ');
    case 'reset_requested': return d.to ? `asked ${d.to.join(', ')}` : '';
    case 'role_created': return `${d.label} (works like ${d.level}) in ${d.group}`;
    case 'role_deleted': return `${d.label || d.role} in ${d.group}`;
    case 'role_permissions_changed':
      return `${d.role} in ${d.group}: ${Object.entries(d.permissions || {}).map(([k, v]) => `${k} ${v === true ? 'on' : v === false ? 'off' : v}`).join(', ')}${d.label ? ` · renamed ${d.label}` : ''}`;
    case 'role_reset_to_defaults': return `${d.role} in ${d.group}`;
    case 'fax_deleted': case 'fax_restored': return `${d.type} · ${(d.faxIds || []).length} record${(d.faxIds || []).length === 1 ? '' : 's'}`;
    case 'email_to_fax_deadline': return d.deadline;
    case 'view_as_action': return `${d.method} ${d.path}`;
    case 'invite_sent': case 'password_reset_by_admin': return d.method === 'temp' ? 'temporary password' : d.method === 'google' ? 'Google' : 'link';
    case 'unregistered_digest_sent': return `${d.emails} email${d.emails === 1 ? '' : 's'}`;
    case 'billing_changed': return d.group || '';
    case 'team_created': case 'team_changed': return `${d.team} · ${d.number || ''}`;
    case 'team_deleted': case 'team_default_changed': return d.team || '';
    case 'person_teams_changed': return `${(d.teams || []).join(', ') || 'no team'}${d.defaultTeam && (d.teams || []).length > 1 ? ` · default ${d.defaultTeam}` : ''}`;
    case 'team_members_changed': return `${d.team}: ${[d.added?.length ? `added ${d.added.join(', ')}` : '', d.removed?.length ? `removed ${d.removed.join(', ')}` : ''].filter(Boolean).join('; ')}`;
    case 'device_remembered': return d.label || '';
    case 'devices_forgotten': return d.all ? 'all devices' : 'one device';
    case 'passkey_added': case 'passkey_signin': return d.label || '';
    case 'backup_code_sent': case 'backup_code_signin': return d.channel === 'whatsapp' ? 'By WhatsApp' : 'By email';
    case 'whatsapp_changed': case 'whatsapp_confirmed': return d.ending ? `Ending ${d.ending}` : 'Removed';
    case 'app_handoff_refused': return d.reason || '';
    case 'app_signin': return d.via || '';
    case 'send_timings_changed': return (d.changes || []).map(c => `${TIMING_NAMES[c.key] || c.key}: ${[].concat(c.from).join('–')} → ${[].concat(c.to).join('–')}`).join(' · ');   // 3.75.0
    case 'passkey_removed': return d.all ? 'all devices' : (d.labels || []).join(', ');
    case 'secondary_email_requested': case 'secondary_email_added': case 'secondary_email_removed': return d.email || '';
    case 'signin_invites_sent': return `${d.trigger === 'weekly' ? 'weekly: ' : ''}${d.sent} sent${d.skipped ? `, ${d.skipped} skipped` : ''}${d.stopped ? `, ${d.stopped} stopped after 4` : ''}${d.failed ? `, ${d.failed} failed` : ''}`;
    case 'twofa_reminders_sent': return `${d.trigger === 'weekly' ? 'weekly: ' : ''}${d.invites} invites, ${d.reminders} reminders${d.skipped ? `, ${d.skipped} skipped` : ''}${d.failed ? `, ${d.failed} failed` : ''}`;
    case 'reminder_schedule_changed': {
      const name = { twofa: 'Two-factor', signup: 'Account sign-up', held: 'Held faxes', inactive: 'Inactive people', requests: 'Access requests' }[d.kind] || d.kind;
      if (d.days) return `${name}: after ${d.days} days`;
      const on = d.on ?? d.weekly;
      return `${name}: ${on ? 'on' : 'off'}${on && d.day ? ` · ${d.day === 'daily' ? 'every day' : d.day}${d.hour != null ? ` ${((d.hour + 11) % 12) + 1}:00 ${d.hour < 12 ? 'AM' : 'PM'} ET` : ''}` : ''}`;
    }
    case 'signed_out_by_admin': return `${d.sessions || 0} open session${d.sessions === 1 ? '' : 's'} closed`;
    case 'people_report_downloaded': return `${d.people} ${d.people === 1 ? 'person' : 'people'}`;
    case 'sender_reminders_sent': return `${d.trigger === 'weekly' ? 'weekly: ' : ''}${d.sent} sent${d.skipped ? `, ${d.skipped} skipped` : ''}${d.failed ? `, ${d.failed} failed` : ''}`;
    case 'removed': return d.group ? `from ${d.group} only` : '';
    case 'access_blocked': return `${d.count || 5} failed faxes to ${d.number || ''}`;
    case 'access_unblocked': return d.number ? `faxes to ${d.number}` : '';
    case 'announcement_sent': return `"${d.title}" to ${d.recipients} ${d.recipients === 1 ? 'person' : 'people'}`;
    case 'announcement_test': return d.subject || '';
    case 'announcement_scheduled': return `"${d.title}" for ${d.at ? new Date(d.at).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET' : ''}${d.repeat && d.repeat !== 'once' ? `, then ${{ weekly: 'every week', biweekly: 'every 2 weeks', monthly: 'every month' }[d.repeat] || d.repeat}${d.until ? ` until ${new Date(d.until).toLocaleDateString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium' })}` : ''}` : ''}`;
    case 'announcement_unscheduled': return `"${d.title}"`;
    case 'email_template_test': case 'email_template_saved': case 'email_template_reset':
      return `${d.template}${d.variant && d.variant !== 'default' ? ` (${d.variant})` : ''}${d.draft ? ', unsaved draft' : ''}`;
    case 'account_created': case 'account_changed': {
      const cap = v => `${v[0].toUpperCase()}${v.slice(1)}`;
      const look = [d.theme && `theme ${cap(d.theme)}`, d.mode && `mode ${cap(d.mode)}`].filter(Boolean).join(', ');
      return `${d.name || d.group || ''}${look ? ` · ${d.appliedTo != null ? `${look} applied to everyone (${d.appliedTo})` : `starting ${look}`}` : ''}`;
    }
    case 'faxes_claimed': return `${d.group}: ${d.faxes} fax${d.faxes === 1 ? '' : 'es'} on ${d.number}${d.failures ? `, ${d.failures} failed` : ''}`;
    case 'kb_article_added': case 'kb_article_edited': case 'kb_article_deleted': return d.title || '';
    case 'held_reminders_sent': case 'inactive_reviews_sent': case 'request_reminders_sent':
      return `${d.trigger === 'weekly' ? 'scheduled: ' : ''}${d.sent ?? d.people ?? d.requests ?? 0} sent${d.failed ? `, ${d.failed} failed` : ''}`;
    case 'signed_in': return d.method || '';
    case 'signed_out': return `${SIGN_OUT[d.reason] || d.reason || ''}${d.duration ? ` · after ${d.duration}` : ''}`;
    case 'tab_opened': return TAB_NAMES[d.tab] || d.tab || '';
    case 'contact_shared_name': return [d.number && fmtFax(d.number), d.name, d.was && `was ${d.was}`].filter(Boolean).join(' · ');
    case 'contact_added': case 'contact_changed': case 'contact_listing_changed':
      return [d.number && fmtFax(d.number), d.name !== undefined && (d.name ? `name ${d.name}` : 'name cleared'), d.was && `was ${d.was}`,
        d.removed !== undefined && (d.removed ? 'removed' : 'restored'), { fax: 'from a fax', compose: 'from Compose' }[d.from]].filter(Boolean).join(' · ');
    case 'fax_sent': return [d.toNumber && `to ${fmtFax(d.toNumber)}`, d.fileName].filter(Boolean).join(' · ');
    case 'app_downloaded': return { 'linux-x86': 'Linux: Intel or AMD (x86)', 'linux-arm': 'Linux: ARM' }[d.app] || d.app || '';   // 3.83.3
    case 'fax_resent': return d.toNumber ? `to ${fmtFax(d.toNumber)}` : '';
    case 'fax_sent_now': return d.toNumber ? `to ${fmtFax(d.toNumber)}` : '';
    case 'microsoft_connected': return d.microsoftAccount || '';
    case 'apple_connected': return d.appleAccount || '';
    case 'fax_viewed': case 'fax_downloaded':
      return [d.type, d.toNumber && `to ${fmtFax(d.toNumber)}`, d.fromNumber && `from ${fmtFax(d.fromNumber)}`, d.fileName].filter(Boolean).join(' · ');
    case 'faxes_downloaded': return `${d.count} fax${d.count === 1 ? '' : 'es'}`;
    case 'report_downloaded': return d.report || '';
    case 'feedback_deleted': return `${d.count} feedback${(d.items || []).length ? ` · ${(d.items || []).slice(0, 3).map(i => i.from).filter(Boolean).join(', ')}${d.count > 3 ? '…' : ''}` : ''}`;
    default: return '';
  }
}

const SIGN_OUT = { sign_out: 'signed out', inactivity: 'timed out (not used for a while)', admin: 'by an Admin' };
const TAB_NAMES = {
  outgoing: 'Outbox', incoming: 'Inbox', sessions: 'Sessions (profile menu)', 'users/sessions': 'Users → Sessions', purged: 'Trash', usage: 'Usage', kb: 'Library', 'logs/failed-sent': 'Logs → Failed Sent', 'logs/failed-received': 'Logs → Failed Received',
  'users/people': 'Users → People', 'users/activity': 'Users → Activity log', 'users/reminders': 'Users → Reminders', 'users/teams': 'Users → Teams',
  'control/accounts': 'Console → Clients', 'control/clients': 'Console → Clients', 'control/roles': 'Console → Roles & Access', 'control/billing': 'Console → Billing', 'control/templates': 'Console → Email Settings → Email Templates', 'control/email-log': 'Console → Email Settings → Email Log', 'control/email-rules': 'Console → Email Settings → Email Rules',
  'control/retries': 'Console → Retry Switches', 'control/fax-retries': 'Console → Retry Switches', 'control/retry-switches': 'Console → Retry Switches', 'control/retention': 'Console → Data Retention',
  'control/rejected-emails': 'Console → Refused Emails', 'control/refused-emails': 'Console → Refused Emails', 'control/blocked-senders': 'Console → Blocked Senders',
  'control/blocked-requests': 'Console → Blocked Connections', 'control/blocked-connections': 'Console → Blocked Connections', 'control/logs': 'Console → Logs',
  // 3.49.0: their own Super Admin pages
  'security/refused-emails': 'Security → Refused Emails', 'security/blocked-senders': 'Security → Blocked Senders', 'security/blocked-connections': 'Security → Blocked Connections',
  'security/ip-blocking': 'Security → IP Blocking', analytics: 'Analytics', 'costs/telnyx': 'Cost Analysis → Telnyx', 'costs/google-cloud': 'Cost Analysis → Google Cloud', health: 'Health',
  'control/costs-telnyx': 'Console → Cost Analysis (Telnyx)', 'control/costs-gcp': 'Console → Cost Analysis (Google Cloud)', 'control/health': 'Console → Health',
};
function fmtFax(num) {
  const n = String(num || '').replace(/\D/g, '');
  return n.length === 11 ? `+${n[0]} (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}` : n.length === 10 ? `+1 (${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}` : num;
}

const KINDS = [['', 'Everything'], ['changes', 'Changes'], ['activity', 'Portal activity']];

export default function ActivityLog() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken } = useAuth();
  const [rows, setRows]   = useState(null);
  const [next, setNext]   = useState(null);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [terms, setTerms] = useState([]);   // "Filter what's shown": several picks (3.16.0)
  // Account column picker (3.18.1); 3.18.2: every account, searched on the
  // server (the list comes with the rows, Super Admins on All accounts)
  const [accounts, setAccounts] = useState(null);
  // Show (3.18.3): the newest 50 by default, like the other lists
  const [filters, setFilters] = useState({ kind: '', email: '', from: '', to: '', account: '', limit: '50' });
  const [applied, setApplied] = useState(filters);

  // Every row (up to 5,000), shown page by page (3.26.0)
  const load = useCallback(async (f) => {
    setError(''); setRows(null);
    try { const r = await fetchActivity(getToken, { ...f, limit: 'all' }); setRows(r.data || []); setNext(r.next || null); setAccounts(r.accounts || null); }
    catch (e) { setError(e.message); setRows([]); }
  }, [getToken]);
  useEffect(() => { load(applied); }, [load, applied]);

  async function more() {
    setLoadingMore(true);
    try { const r = await fetchActivity(getToken, { ...applied, limit: 'all', before: next }); setRows(x => [...(x || []), ...(r.data || [])]); setNext(r.next || null); }
    catch (e) { setError(e.message); }
    setLoadingMore(false);
  }
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const apply = f => { setFilters(f); setApplied(f); };
  const filtered = !!(applied.kind || applied.email || applied.from || applied.to || applied.account);

  // Suggestions for the filter: the people and actions in what's loaded
  const termOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach(ev => { [ev.actor, ev.target, ACTIONS[ev.action] || ev.action].forEach(v => { if (v) set.add(String(v)); }); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [rows]);
  // Picks of the same kind are alternatives (either person, either action);
  // picks of different kinds must all match (3.18.1: this person AND this
  // action). Kinds: a person (who / about), an action, or any other text
  // (matched anywhere in the row).
  const filteredRows = useMemo(() => {
    const people = new Set(), actions = new Set();
    (rows || []).forEach(ev => {
      [ev.actor, ev.target].forEach(v => { if (v) people.add(String(v).toLowerCase()); });
      actions.add(String(ACTIONS[ev.action] || ev.action || '').toLowerCase());
    });
    const ts = terms.map(t => String(t).trim().toLowerCase()).filter(Boolean);
    const groups = { person: [], action: [], text: [] };
    ts.forEach(t => groups[people.has(t) ? 'person' : actions.has(t) ? 'action' : 'text'].push(t));
    return (rows || []).filter(ev => {
      const who = [ev.actor, ev.target].map(x => String(x || '').toLowerCase());
      const what = String(ACTIONS[ev.action] || ev.action || '').toLowerCase();
      if (groups.person.length && !groups.person.some(t => who.includes(t))) return false;
      if (groups.action.length && !groups.action.includes(what)) return false;
      if (groups.text.length && !groups.text.some(t => [ev.actor, ev.target, ACTIONS[ev.action], describe(ev), ev.accountName]
        .some(x => String(x || '').toLowerCase().includes(t)))) return false;
      return true;
    });
  }, [rows, terms]);
  // Sorting and Excel (3.16.0)
  const { sorted: shown, sort, onSort } = useSort(filteredRows, {
    when: ev => ev.at || '', who: ev => ev.actor, account: ev => ev.accountName || '', what: ev => ACTIONS[ev.action] || ev.action, about: ev => (ev.target && ev.target !== ev.actor ? ev.target : ''),
  });
  const { pageRows, pager } = usePager(shown, filters.limit);   // page by page (3.26.0)
  async function downloadXlsx() {
    try {
      await downloadSheet('Activity Log', [
        { header: 'When', get: ev => formatDate(ev.at), width: 24 },
        { header: 'Who', get: ev => `${ev.actor || ''}${ev.detail?.viewAs ? ` (viewing as ${ev.detail.viewAs})` : ''}`, width: 30 },
        { header: clientWord, get: ev => ev.accountName || '', width: 18 },
        { header: 'What', get: ev => ACTIONS[ev.action] || ev.action, width: 32 },
        { header: 'About', get: ev => (ev.target && ev.target !== ev.actor ? ev.target : ''), width: 30 },
        { header: 'Details', get: ev => describe(ev) || '', width: 50 },
      ], shown, getToken);
    } catch (e) { setError(e.message); }
  }

  return (
    <div>
      <div style={S.top}>
        <div>
          <h3 style={S.title}>Activity log</h3>
          <p style={S.sub}>Changes to people, roles, settings and faxes, and what people do in the portal (signing in, sending, downloading), newest first. It can't be edited.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={downloadXlsx} disabled={!shown.length} style={{ ...S.refreshBtn, opacity: shown.length ? 1 : 0.5 }} title="Download what's shown as an Excel file"><Emo e="⬇" /> Download (.xlsx)</button>
          <button onClick={() => load(applied)} style={S.refreshBtn}><Emo e="↻" /> Refresh</button>
        </div>
      </div>

      <div style={S.filters}>
        <div role="tablist" aria-label="Show" style={S.kinds}>
          {KINDS.map(([k, l]) => (
            <button key={k || 'all'} role="tab" aria-selected={filters.kind === k} onClick={() => apply({ ...filters, kind: k })}
              style={{ ...S.kind, ...(filters.kind === k ? S.kindOn : {}) }}>{l}</button>
          ))}
        </div>
        <form onSubmit={e => { e.preventDefault(); apply(filters); }} style={S.filterForm}>
          <input value={filters.email} onChange={e => set('email', e.target.value)} placeholder="Person's email" aria-label="Person's email" style={{ ...S.search, minWidth: 200 }} />
          <label style={S.dateLabel}>From <input type="date" value={filters.from} onChange={e => set('from', e.target.value)} style={S.date} /></label>
          <label style={S.dateLabel}>To <input type="date" value={filters.to} onChange={e => set('to', e.target.value)} style={S.date} /></label>
          <button type="submit" style={S.applyBtn}>Search</button>
          <ShowSelect value={filters.limit} onChange={v => apply({ ...filters, limit: v })}
            groupStyle={S.dateLabel} labelStyle={{}} style={S.date} />
          {filtered && <button type="button" onClick={() => apply({ kind: '', email: '', from: '', to: '', account: '', limit: filters.limit })} style={S.clearBtn}>Clear filters</button>}
        </form>
        <MultiSearchSelect values={terms} onChange={setTerms} options={termOptions} placeholder="Filter what's shown" />
      </div>

      {error && <div role="alert" style={S.error}>{error}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="when" label="When" sort={sort} onSort={onSort} style={S.th} /><SortTh k="who" label="Who" sort={sort} onSort={onSort} style={S.th} />
            {accounts ? (
              <th style={S.th}>
                <select value={filters.account} onChange={e => apply({ ...filters, account: e.target.value })} aria-label={clientWord} style={S.thSelect}>
                  <option value="" style={S.thOption}>{clientWord}: All</option>
                  {accounts.map(a => <option key={a.id} value={a.id} style={S.thOption}>{a.name}</option>)}
                </select>
              </th>
            ) : <SortTh k="account" label={clientWord} sort={sort} onSort={onSort} style={S.th} />}
            <SortTh k="what" label="What" sort={sort} onSort={onSort} style={S.th} /><SortTh k="about" label="About" sort={sort} onSort={onSort} style={S.th} /><th style={S.th}>Details</th>
          </tr></thead>
          <tbody>
            {!rows ? <tr><td colSpan={6} style={S.empty}><Loader label="Loading the activity log…" /></td></tr>
              : !shown.length ? <tr><td colSpan={6} style={S.empty}>{filtered || terms.length ? 'Nothing matches these filters.' : 'Nothing recorded yet.'}</td></tr>
              : pageRows.map((ev, i) => (
                <tr key={`${ev.at}-${i}`} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(ev.at)}</td>
                  <td style={S.td}>{ev.actor || '—'}{ev.detail?.viewAs && <div style={S.viewAs}>viewing as {ev.detail.viewAs}</div>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{ev.accountName || '—'}</td>
                  <td style={S.td}>{ev.kind === 'activity' && <span style={S.activityTag}>Activity</span>} {ACTIONS[ev.action] || ev.action}</td>
                  <td style={S.td}>{ev.target && ev.target !== ev.actor ? ev.target : '—'}</td>
                  <td style={{ ...S.td, color: 'var(--t-607d8b)', fontSize: 12 }}>{describe(ev) || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {rows && <Pager {...pager} label="entries" />}
      {rows && next && (
        <div style={S.moreRow}><button onClick={more} disabled={loadingMore} style={S.moreBtn}>{loadingMore ? 'Loading…' : 'Load more'}</button></div>
      )}
    </div>
  );
}

const S = {
  top: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 12 },
  title: SECTION_TITLE,
  sub: SECTION_SUB,
  search: { padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, minWidth: 220, fontFamily: 'inherit' },
  filters: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 },
  kinds: PILL_ROW,
  kind: PILL,
  kindOn: PILL_ON,
  filterForm: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  dateLabel: { display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--t-607d8b)', fontWeight: 600 },
  date: { padding: '7px 8px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit' },
  applyBtn: { padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  clearBtn: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  activityTag: { display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)', marginRight: 4 },
  viewAs: { fontSize: 11, color: 'var(--t-6a1b9a)' },
  moreRow: { display: 'flex', justifyContent: 'center', margin: '12px 0' },
  moreBtn: { padding: '8px 20px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-80cbc4)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  refreshBtn: { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, marginBottom: 10, fontSize: 13 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  thSelect: TH_SELECT,
  thOption: TH_OPTION,
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '9px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top', overflowWrap: 'anywhere' },
  empty: { textAlign: 'center', padding: '36px 0', color: 'var(--t-90a4ae)', fontSize: 14 },
};
